import type { StoredTokenData, TokenData } from './tokenTypes';

/**
 * Token storage service
 * Manages access and refresh tokens in localStorage with expiration times
 */

const TOKEN_STORAGE_KEY = 'auth_tokens';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class TokenStorage {
  /**
   * Store token data in localStorage
   */
  static setTokens(tokenData: TokenData): void {
    try {
      const storedData: StoredTokenData = {
        ...tokenData,
        storedAt: Date.now(),
      };
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(storedData));
    } catch (error) {
      console.error('Failed to store tokens:', error);
    }
  }

  /**
   * Get stored token data from localStorage
   */
  static getTokens(): StoredTokenData | null {
    try {
      const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!stored) {
        return null;
      }
      return JSON.parse(stored) as StoredTokenData;
    } catch (error) {
      console.error('Failed to retrieve tokens:', error);
      return null;
    }
  }

  /**
   * Get access token if it's valid (not expired)
   */
  static getAccessToken(): string | null {
    const tokens = this.getTokens();
    if (!tokens) {
      return null;
    }

    // Check if access token is expired (with 30 second buffer)
    const now = Math.floor(Date.now() / 1000);
    if (now >= tokens.accessTokenExp - 30) {
      return null;
    }

    return tokens.accessToken;
  }

  /**
   * Get refresh token if it's valid (not expired)
   */
  static getRefreshToken(): string | null {
    const tokens = this.getTokens();
    if (!tokens) {
      return null;
    }

    // Check if refresh token is expired
    const now = Math.floor(Date.now() / 1000);
    if (now >= tokens.refreshTokenExp) {
      return null;
    }

    return tokens.refreshToken;
  }

  /**
   * Check if access token is expired or will expire soon (within 5 minutes)
   */
  static isAccessTokenExpiring(): boolean {
    const tokens = this.getTokens();
    if (!tokens) {
      return true;
    }

    // Check if token expires within 5 minutes (300 seconds)
    const now = Math.floor(Date.now() / 1000);
    const fiveMinutes = 5 * 60;
    return now >= tokens.accessTokenExp - fiveMinutes;
  }

  /**
   * Check if refresh token is expired
   */
  static isRefreshTokenExpired(): boolean {
    const tokens = this.getTokens();
    if (!tokens) {
      return true;
    }

    const now = Math.floor(Date.now() / 1000);
    return now >= tokens.refreshTokenExp;
  }

  /**
   * Clear all stored tokens
   */
  static clearTokens(): void {
    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      // Clear legacy token storage keys
      localStorage.removeItem('mpn_access_token');
      localStorage.removeItem('mpn_refresh_token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('token');
    } catch (error) {
      console.error('Failed to clear tokens:', error);
    }
  }

  /**
   * Get token expiration times for debugging
   */
  static getTokenExpirationInfo(): {
    accessTokenExp?: Date;
    refreshTokenExp?: Date;
    accessTokenExpiresIn?: number;
    refreshTokenExpiresIn?: number;
  } {
    const tokens = this.getTokens();
    if (!tokens) {
      return {};
    }

    const now = Math.floor(Date.now() / 1000);

    return {
      accessTokenExp: new Date(tokens.accessTokenExp * 1000),
      refreshTokenExp: new Date(tokens.refreshTokenExp * 1000),
      accessTokenExpiresIn: Math.max(0, tokens.accessTokenExp - now),
      refreshTokenExpiresIn: Math.max(0, tokens.refreshTokenExp - now),
    };
  }
}
